import React, { useState, useEffect } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate } from "react-router-dom";
import { getRolePermissions, storeRole } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const CreateRole = () => {
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPermissions = async () => {
      setIsLoadingPermissions(true);
      try {
        const response = await getRolePermissions();
        setPermissions(response.data);
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  const initialValues = {
    role: "",
    permissions: [],
  };

  const validationSchema = Yup.object().shape({
    role: Yup.string().required("Role name is required"),
    permissions: Yup.array().min(1, "At least one permission must be selected"),
  });

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmittingForm(true);
    try {
      const payload = {
        role: values.role,
        permissions: values.permissions,
      };

      await storeRole(payload);
      toast.success("Role created successfully!");
      navigate("/roles");
    } catch (error) {
      console.error("Failed to create role:", error);
      toast.error(error.message || "Failed to create role. Please try again.");
      setErrors({ submit: "Failed to create role" });
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, values, setFieldValue }) => (
          <Form>
            {isSubmittingForm && (
              <div className="form-overlay">
                <Skeleton height={40} />
              </div>
            )}
            <div className="card border-light">
              <div className="card-body">
                {errors.submit && (
                  <div className="alert alert-danger">{errors.submit}</div>
                )}
                <div className="row">
                  {/* Role Name */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="role" className="form-label">
                      <span className="text-danger">*</span> Role Name
                    </label>
                    <Field
                      name="role"
                      as="input"
                      type="text"
                      placeholder="Enter role name"
                      className={`form-control ${
                        touched.role && errors.role ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="role"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  <div className="col-md-6 mb-3 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={() => {
                        const allPermissions = permissions.map(permission => `${permission.group}:${permission.name}`);
                        setFieldValue("permissions", allPermissions);
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setFieldValue("permissions", [])}
                    >
                      Select None
                    </button>
                  </div>
                </div>
                <div className="row">
                  {/* Permissions */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      <span className="text-danger">*</span> Permissions
                    </label>
                    {isLoadingPermissions ? (
                      <Skeleton height={40} count={10} />
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th style={{ width: "30%" }}>Group</th>
                              <th style={{ overflowX: "auto", display: "block" }}>Permissions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {permissions.reduce((acc, permission) => {
                              const groupIndex = acc.findIndex(
                                (group) => group.group === permission.group
                              );
                              if (groupIndex === -1) {
                                acc.push({
                                  group: permission.group,
                                  permissions: [permission],
                                });
                              } else {
                                acc[groupIndex].permissions.push(permission);
                              }
                              return acc;
                            }, []).map((group) => {
                              const allActions = Array.from(new Set(permissions.map(permission => permission.name)));
                              return (
                                <tr key={group.group}>
                                  <td>{group.group}</td>
                                  <td style={{ overflowX: "auto", display: "block" }}>
                                    {allActions.map((action) => {
                                      const permission = group.permissions.find(
                                        (perm) => perm.name === action
                                      );
                                      return (
                                        <div key={action} className="form-check form-check-inline">
                                          <Field
                                            type="checkbox"
                                            name="permissions"
                                            value={permission ? `${group.group}:${permission.name}` : ""}
                                            checked={permission && values.permissions.includes(`${group.group}:${permission.name}`)}
                                            onChange={() => {
                                              if (permission) {
                                                if (values.permissions.includes(`${group.group}:${permission.name}`)) {
                                                  setFieldValue(
                                                    "permissions",
                                                    values.permissions.filter(
                                                      (p) => p !== `${group.group}:${permission.name}`
                                                    )
                                                  );
                                                } else {
                                                  setFieldValue(
                                                    "permissions",
                                                    [...values.permissions, `${group.group}:${permission.name}`]
                                                  );
                                                }
                                              }
                                            }}
                                            className="form-check-input"
                                            disabled={!permission}
                                          />
                                          <label
                                            className={`form-check-label ${!permission ? "text-muted" : ""}`}
                                          >
                                            {action}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <ErrorMessage
                      name="permissions"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn me-3 ansa-button-secondary"
                    onClick={() => navigate("/roles")}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn ansa-button-primary"
                    disabled={isSubmitting}
                  >
                    Create Role
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateRole;
